import * as React from "react";
import type { ParseKeys, TFunction } from "i18next";
import { useTranslation } from "react-i18next";

/**
 * Returns a ref that you can assign to an element.
 *
 * When doing so, it will use the DOM constraint validation API to return a
 * localized error message and a boolean indicating whether or not the control
 * is invalid.
 */
export function useDOMConstraintValidation() {
	const [ref, setRef] = React.useState<
		HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null
	>(null);
	const { t } = useTranslation(["validation"]);
	const [didInvalidFire, setDidInvalidFire] = React.useState<boolean>(false);
	const [didBlurFire, setDidBlurFire] = React.useState<boolean>(false);
	const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
	const isInvalid = Boolean(errorMessage);

	const [, startTransition] = React.useTransition();

	React.useEffect(() => {
		if (!ref) {
			return;
		}

		const onInvalid = () => {
			if (ref.form?.noValidate) {
				return;
			}
			setDidInvalidFire(true);
			updateValidity();
		};

		const onChange = (event: Event) => {
			if (event.target !== ref) {
				return;
			}
			if (ref.form?.noValidate) {
				return;
			}
			if (!didBlurFire && !didInvalidFire) {
				return;
			}
			updateValidity();
		};

		const onBlur = () => {
			if (ref.form?.noValidate) {
				return;
			}
			updateValidity();
			setDidBlurFire(true);
		};

		const updateValidity = () => {
			// The message is React state, so setting it re-renders the control.
			// Keep that off the keystroke's urgent lane. The custom validity flag
			// is not React state, so it stays outside the transition.
			// https://linear.app/bigplans/issue/DE-659/kelvin-controlled-inputs-require-multiple-keystrokes-to-accept-inputs
			if (ref.validity.valid || ref.disabled) {
				ref.setCustomValidity("");
				startTransition(() => setErrorMessage(undefined));
			} else if (!ref.validity.valid) {
				const errorMessage = getCustomValidity({
					control: ref,
					t,
				});
				ref.setCustomValidity(errorMessage ?? "");
				startTransition(() => setErrorMessage(errorMessage ?? ""));
			}
		};

		if (!ref.form?.noValidate) {
			ref.addEventListener("invalid", onInvalid);
			// `input` and `change` bubble. Listen on document, not on the
			// control. React's own listener is on document and runs first.
			// A listener on the control runs before that, and React 19 then
			// paints this error update before it has read the keystroke, which
			// puts the old value back and the letter disappears.
			document.addEventListener("change", onChange);
			document.addEventListener("input", onChange);
			ref.addEventListener("blur", onBlur);
		}

		return () => {
			ref.removeEventListener("invalid", onInvalid);
			document.removeEventListener("change", onChange);
			document.removeEventListener("input", onChange);
			ref.removeEventListener("blur", onBlur);
		};
	}, [ref, didInvalidFire, didBlurFire, t]);

	return [setRef, errorMessage, isInvalid] as const;
}

const validationTransKeyFromValidityStateMap: Record<
	keyof Omit<ValidityState, "valid" | "customError">,
	Record<string, ParseKeys<["validation"]>>
> = {
	valueMissing: {
		"input:radio": "validation:value_missing_radio",
		"input:checkbox": "validation:value_missing_checkbox",
		"input:hidden": "validation:value_missing_file",
		select: "validation:value_missing_select",
		default: "validation:value_missing",
	},
	typeMismatch: {
		"input:url": "validation:type_mismatch_url",
		"input:email": "validation:type_mismatch_email",
		default: "validation:type_mismatch",
	},
	patternMismatch: {
		default: "validation:pattern_mismatch",
	},
	tooLong: {
		default: "validation:too_long",
	},
	tooShort: {
		default: "validation:too_short",
	},
	rangeUnderflow: {
		"input:date": "validation:range_underflow_date",
		default: "validation:range_underflow",
	},
	rangeOverflow: {
		"input:date": "validation:range_overflow_date",
		default: "validation:range_overflow",
	},
	stepMismatch: {
		default: "validation:step_mismatch",
	},
	badInput: {
		default: "validation:bad_input",
	},
};

/**
 * Returns a localized string for the given control's ValidityState.
 *
 * If control is valid, returns undefined.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
 */
export function getCustomValidity({
	control,
	t,
}: {
	control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
	t: TFunction<["validation"]>;
}): string | undefined {
	let min: string | undefined;
	let max: string | undefined;
	let minLength: number | undefined;
	let maxLength: number | undefined;

	if ("min" in control) {
		min =
			control.type === "date"
				? new Date(String(control.min)).toLocaleDateString()
				: control.min;
	}
	if ("max" in control) {
		max =
			control.type === "date"
				? new Date(String(control.max)).toLocaleDateString()
				: control.max;
	}
	if ("maxLength" in control) {
		maxLength = control.maxLength;
	}
	if ("minLength" in control) {
		minLength = control.minLength;
	}

	if (control.validity.valid === true) {
		return undefined;
	}

	const order: Array<keyof Omit<ValidityState, "valid" | "customError">> = [
		"tooLong",
		"tooShort",
		"rangeUnderflow",
		"rangeOverflow",
		"patternMismatch",
		"stepMismatch",
		"badInput",
		"typeMismatch",
		"valueMissing",
	];

	const invalidKey = order.find((key) => control.validity[key] === true);

	if (!invalidKey) {
		return undefined;
	}

	const tagName = control.tagName.toLowerCase();
	const transKey: ParseKeys<["validation"]> | undefined =
		validationTransKeyFromValidityStateMap[invalidKey][
			`${tagName}:${control.type}`
		] ??
		validationTransKeyFromValidityStateMap[invalidKey][tagName] ??
		validationTransKeyFromValidityStateMap[invalidKey]["default"];

	if (!transKey) {
		return t("validation:unknown");
	}

	switch (transKey) {
		case "validation:too_short": {
			return t(transKey, { count: minLength });
		}
		case "validation:too_long": {
			return t(transKey, { count: maxLength });
		}
		case "validation:range_overflow": {
			return t(transKey, { max: max });
		}
		case "validation:range_overflow_date": {
			return t(transKey, { max: max });
		}
		case "validation:range_underflow": {
			return t(transKey, { min: min });
		}
		case "validation:range_underflow_date": {
			return t(transKey, { min: min });
		}
		default: {
			return t(transKey);
		}
	}
}
